import React from 'react'
import PropTypes from 'prop-types'
import { isMobile } from 'react-device-detect'
import { Card, Col, Row } from 'antd'
import { RightCircleOutlined } from '@ant-design/icons'

import { useStores } from '../../../../cls/stores/MobXProvider'
import { useSmartTranslation } from '../../../../cls/TranslationContext'
/**
 * @component RenderTabsContent
 * @description
 * Layout component that renders an array of React children in a responsive grid using Ant Design's `Row` and `Col`.
 * Automatically adjusts columns for mobile screens (1 per row) vs desktop (3 per row).
 *
 * @param {Object} props
 * @param {React.ReactElement[]} props.children - Array of card components to render in the layout.
 *
 * @returns {React.ReactElement} A wrapped grid layout of children.
 */
const RenderTabsContent = ({ children }) => {
	return (
		<Row gutter={[16, 16]}>
			{children.map((child, index) => (
				<Col key={index} span={isMobile ? 24 : 8}>
					{child}
				</Col>
			))}
		</Row>
	)
}
RenderTabsContent.propTypes = {
	children: PropTypes.array
}

// Design
/**
 * @function renderDetails
 * @description
 * Renders a single required action card containing a label and a link.
 *
 * @param {Object} action
 * @param {string} action.id - Unique ID of the action.
 * @param {string} action.label - Display label (translated) for the action.
 * @param {string} action.link - Target URL or internal route for the action.
 *
 * @returns {React.ReactElement} A styled `<Card>` representing one onboarding action.
 */
const renderDetails = (action) => {
	return (
		<Card
			key={action.id}
			variant="borderless"
			style={{ background: '#f6f8fa', boxShadow: 'none' }}
		>
			<div>
				<strong>{action.label}</strong>
			</div>
			<a
				style={{
					fontSize: '20px'
				}}
				href={action.link}
				rel="noreferrer"
			>
				<RightCircleOutlined />
			</a>
		</Card>
	)
}

/**
 * @component GetStarted
 * @description
 * Displays a "Get Started" section for new users based on their `requiredActions`.
 * Each action is shown as a card with a title and link icon (`RightCircleOutlined`) leading to the relevant resource.
 *
 * The section is only shown if the currently logged-in user has pending onboarding or required setup actions.
 *
 * @returns {React.ReactElement|null} A responsive section of onboarding action cards, or `null` if no actions are required.
 *
 * @features
 * - Responsive grid layout using Ant Design's `<Row>` and `<Col>`.
 * - Conditional rendering based on `loggedInUser.requiredActions`.
 * - Uses `mobx-react-lite` for MobX state.
 * - Translated UI text via `react-i18next`.
 * - Mobile-friendly layout using `react-device-detect`.
 *
 * @example
 * <GetStarted />
 */
const GetStarted = () => {
	const { t } = useSmartTranslation()
	const { users } = useStores()
	const { loggedInUser } = users
	// console.log('loggedInUser', toJS(loggedInUser))

	const requiredActions = loggedInUser.requiredActions
	// console.log('requiredActions', requiredActions)
	if (requiredActions.length === 0) {
		return null
	}
	return (
		<div id="target-get-started">
			<h3>{t('single.get-started.label')}</h3>
			<p>{t('single.get-started.recommended-cards')}</p>
			<RenderTabsContent>
				{requiredActions.map(renderDetails)}
			</RenderTabsContent>
		</div>
	)
}

export default GetStarted
